/**
 * AEO Benchmarks Page
 *
 * Full page view of all AEO competitive benchmark comparisons with:
 * - Performance over time charts
 * - Sports-style matchup cards
 * - Filtering and pagination
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import {
  Trophy,
  TrendingUp,
  TrendingDown,
  Target,
  ArrowLeft,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Globe,
  Crown,
  Package
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Bar
} from 'recharts';

// Types
interface Scores {
  aeo: number;
  seo: number;
  ux: number;
  overall: number;
}

interface ProductData {
  name: string;
  imageUrl: string | null;
  brand: string | null;
}

interface SerpLeaderData {
  title: string;
  url: string;
  position: number;
  score: number;
}

interface Analysis {
  id: string;
  type: 'generated' | 'serp_leader' | 'current_page' | 'competitor';
  label: string;
  pageTitle: string;
  pageUrl: string;
  serpPosition: number | null;
  scores: Scores;
  strengths: { aeo: string[]; seo: string[] };
  weaknesses: { aeo: string[]; seo: string[] };
}

interface Benchmark {
  id: string;
  title: string;
  sourceType: 'product' | 'category';
  sourceId: string;
  serpQuery: string;
  serpCountry: string;
  createdAt: string;
  createdBy: string;
  isWinner: boolean;
  verdict: string;
  rankings: Record<string, Record<number, string>>;
  product: ProductData | null;
  serpLeader: SerpLeaderData | null;
  scores: Scores | null;
  analyses: Analysis[];
}

interface ChartDataPoint {
  date: string;
  comparisons: number;
  avgScore: number;
  avgAeoScore: number;
  wins: number;
}

interface BenchmarksData {
  benchmarks: Benchmark[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  chartData: ChartDataPoint[];
  periodDays: number;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatFullDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

// Sports-style matchup card
function MatchupCard({ benchmark, expanded, onToggle }: {
  benchmark: Benchmark;
  expanded: boolean;
  onToggle: () => void;
}) {
  const ourScore = benchmark.scores?.overall || 0;
  const theirScore = benchmark.serpLeader?.score || 0;
  const isWinning = ourScore > theirScore;
  const generatedAnalysis = benchmark.analyses.find(a => a.type === 'generated');

  return (
    <Card className="overflow-hidden">
      {/* Sports-style scoreboard header */}
      <div
        className="relative bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4 cursor-pointer border-b border-slate-200 dark:border-slate-700"
        onClick={onToggle}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-slate-400 dark:bg-white" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 border-2 border-slate-400 dark:border-white rounded-full" />
        </div>

        {/* Winner badge */}
        {benchmark.isWinner && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-amber-500 text-white border-0">
              <Trophy className="h-3 w-3 mr-1" />
              WIN
            </Badge>
          </div>
        )}

        <div className="relative flex items-center justify-between gap-4">
          {/* Home Team - Our AEO Page (Product) */}
          <div className="flex-1 flex flex-col items-center text-center">
            <div className="relative">
              {benchmark.product?.imageUrl ? (
                <img
                  src={benchmark.product.imageUrl}
                  alt={benchmark.product.name || 'Product'}
                  className="w-14 h-14 rounded-xl object-cover border-2 border-amber-400 shadow-lg shadow-amber-400/20"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center ${benchmark.product?.imageUrl ? 'hidden' : ''}`}>
                <Package className="h-7 w-7 text-white" />
              </div>
              {isWinning && (
                <Crown className="absolute -top-2 -right-2 h-4 w-4 text-amber-400 drop-shadow-lg" />
              )}
            </div>
            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-2 uppercase tracking-wide">Your AEO</span>
            <span className="text-xs text-slate-600 dark:text-slate-400 truncate max-w-[120px]" title={benchmark.product?.name || benchmark.title}>
              {(benchmark.product?.name || benchmark.title).substring(0, 25)}
              {(benchmark.product?.name || benchmark.title).length > 25 ? '...' : ''}
            </span>
          </div>

          {/* Scores */}
          <div className="flex items-center gap-3">
            <div className={`text-4xl font-bold tabular-nums ${isWinning ? 'text-green-600 dark:text-green-400' : 'text-slate-700 dark:text-white'}`}>
              {Math.round(ourScore)}
            </div>
            <div className="flex flex-col items-center">
              <span className="text-slate-400 dark:text-slate-500 text-xs uppercase tracking-wider">vs</span>
              <div className="w-8 h-px bg-slate-300 dark:bg-slate-600 my-1" />
            </div>
            <div className={`text-4xl font-bold tabular-nums ${!isWinning && theirScore > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-700 dark:text-white'}`}>
              {Math.round(theirScore)}
            </div>
          </div>

          {/* Away Team - SERP Leader */}
          <div className="flex-1 flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-blue-400 shadow-lg shadow-blue-400/20">
                <Globe className="h-7 w-7 text-white" />
              </div>
              {!isWinning && theirScore > ourScore && (
                <Crown className="absolute -top-2 -right-2 h-4 w-4 text-blue-400 drop-shadow-lg" />
              )}
            </div>
            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-2 uppercase tracking-wide">SERP #1</span>
            <span className="text-xs text-slate-600 dark:text-slate-400 truncate max-w-[120px]" title={benchmark.serpLeader?.title || 'SERP Leader'}>
              {(benchmark.serpLeader?.title || 'SERP Leader').substring(0, 25)}
              {(benchmark.serpLeader?.title || '').length > 25 ? '...' : ''}
            </span>
          </div>

          {/* Expand button */}
          <Button variant="ghost" size="sm" className="absolute bottom-2 right-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {/* Score breakdown */}
        <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
          <span>AEO: {benchmark.scores?.aeo.toFixed(0) || 0}</span>
          <span>SEO: {benchmark.scores?.seo.toFixed(0) || 0}</span>
          <span>UX: {benchmark.scores?.ux.toFixed(0) || 0}</span>
        </div>
        <div className="text-xs text-slate-500 text-center mt-1">
          {formatFullDate(benchmark.createdAt)} {benchmark.serpQuery && `• "${benchmark.serpQuery.substring(0, 40)}${benchmark.serpQuery.length > 40 ? '...' : ''}"`}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <CardContent className="pt-4 space-y-4">
          {/* All competitors comparison */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Full Comparison</h4>
            {benchmark.analyses.map((analysis, idx) => {
              const maxScore = Math.max(...benchmark.analyses.map(a => a.scores.overall), 1);
              const barWidth = (analysis.scores.overall / maxScore) * 100;
              const isGenerated = analysis.type === 'generated';

              return (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{analysis.label}</span>
                      {analysis.serpPosition && (
                        <Badge variant="outline" className="text-xs">
                          #{analysis.serpPosition}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={getScoreColor(analysis.scores.overall)}>
                        {analysis.scores.overall.toFixed(0)}
                      </span>
                      {analysis.pageUrl && (
                        <a
                          href={analysis.pageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="h-6 bg-muted rounded-full overflow-hidden relative">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isGenerated
                          ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                          : analysis.type === 'serp_leader'
                            ? 'bg-blue-400'
                            : analysis.type === 'current_page'
                              ? 'bg-purple-400'
                              : 'bg-gray-400'
                      }`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <div className="flex gap-4 text-xs text-muted-foreground pl-2">
                    <span>AEO: {analysis.scores.aeo.toFixed(0)}</span>
                    <span>SEO: {analysis.scores.seo.toFixed(0)}</span>
                    <span>UX: {analysis.scores.ux.toFixed(0)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Strengths and Weaknesses */}
          {generatedAnalysis && (
            <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <h4 className="text-sm font-medium text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                  <TrendingUp className="h-4 w-4" />
                  Strengths
                </h4>
                <div className="space-y-1">
                  {(generatedAnalysis.strengths?.aeo || []).slice(0, 3).map((s, i) => (
                    <div key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                      <span className="text-green-500">+</span>
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2 flex items-center gap-1">
                  <TrendingDown className="h-4 w-4" />
                  Areas to Improve
                </h4>
                <div className="space-y-1">
                  {(generatedAnalysis.weaknesses?.aeo || []).slice(0, 3).map((w, i) => (
                    <div key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                      <span className="text-red-500">-</span>
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Verdict */}
          {benchmark.verdict && (
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-1">AI Verdict</h4>
              <p className="text-sm text-muted-foreground italic">"{benchmark.verdict.split('\n')[0]}"</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function AEOBenchmarks() {
  const navigate = useNavigate();
  const { selectedSite } = useSite();
  const [data, setData] = useState<BenchmarksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [days, setDays] = useState(30);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSite) {
      fetchBenchmarks();
    }
  }, [selectedSite, page, days]);

  const fetchBenchmarks = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(
        `/api/content-manager-dashboard/site/${siteId}/aeo-benchmarks?page=${page}&days=${days}&limit=10`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch benchmarks');
      }

      const result = await response.json();
      if (result.success) {
        setData(result);
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (err) {
      console.error('Error fetching benchmarks:', err);
      setError(err instanceof Error ? err.message : 'Failed to load benchmarks');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats from chart data
  const summaryStats = useMemo(() => {
    if (!data?.chartData || data.chartData.length === 0) {
      return { totalComparisons: 0, totalWins: 0, avgScore: 0, winRate: 0 };
    }

    const totals = data.chartData.reduce(
      (acc, point) => ({
        comparisons: acc.comparisons + (point.comparisons || 0),
        wins: acc.wins + (point.wins || 0),
        scoreSum: acc.scoreSum + ((point.avgScore || 0) * (point.comparisons || 0)),
      }),
      { comparisons: 0, wins: 0, scoreSum: 0 }
    );

    return {
      totalComparisons: totals.comparisons,
      totalWins: totals.wins,
      avgScore: totals.comparisons > 0 ? Math.round(totals.scoreSum / totals.comparisons) : 0,
      winRate: totals.comparisons > 0 ? Math.round((totals.wins / totals.comparisons) * 100) : 0
    };
  }, [data?.chartData]);

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Site Selected</h3>
            <p className="text-muted-foreground text-center">
              Please select a site to view AEO benchmarks.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Trophy className="h-6 w-6 text-amber-500" />
              AEO Benchmarks
            </h1>
            <p className="text-muted-foreground">
              Head-to-head matchups against SERP leaders
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Select value={days.toString()} onValueChange={(v) => { setDays(parseInt(v)); setPage(1); }}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchBenchmarks}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-blue-600">{summaryStats.totalComparisons}</div>
            <div className="text-sm text-muted-foreground">Total Matchups</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className={`text-3xl font-bold ${summaryStats.winRate >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
              {summaryStats.winRate}%
            </div>
            <div className="text-sm text-muted-foreground">Win Rate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-amber-600">{summaryStats.totalWins}</div>
            <div className="text-sm text-muted-foreground">Total Wins</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className={`text-3xl font-bold ${getScoreColor(summaryStats.avgScore)}`}>
              {summaryStats.avgScore}
            </div>
            <div className="text-sm text-muted-foreground">Avg Score</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      {data?.chartData && data.chartData.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Score Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                Score Trend
              </CardTitle>
              <CardDescription>Average scores over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tickFormatter={formatDate} className="text-xs" />
                    <YAxis domain={[0, 100]} className="text-xs" />
                    <Tooltip
                      labelFormatter={(label) => formatDate(label)}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="avgScore" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} name="Overall Score" />
                    <Line type="monotone" dataKey="avgAeoScore" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="AEO Score" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Wins & Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-green-500" />
                Activity & Wins
              </CardTitle>
              <CardDescription>Matchups and wins per day</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tickFormatter={formatDate} className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      labelFormatter={(label) => formatDate(label)}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="comparisons" fill="#94a3b8" name="Matchups" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="wins" fill="#22c55e" name="Wins" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Matchups List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              All Matchups
            </span>
            {data?.pagination && (
              <span className="text-sm font-normal text-muted-foreground">
                {data.pagination.total} total
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchBenchmarks} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : !data?.benchmarks || data.benchmarks.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No Matchups Found</h3>
              <p className="text-muted-foreground mb-4">
                No competitive benchmarks in the selected time period.
              </p>
              <Button variant="outline" onClick={() => navigate('/about-business/offering?tab=categories')}>
                Generate AEO Content
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {data.benchmarks.map((benchmark) => (
                <MatchupCard
                  key={benchmark.id}
                  benchmark={benchmark}
                  expanded={expandedId === benchmark.id}
                  onToggle={() => setExpandedId(expandedId === benchmark.id ? null : benchmark.id)}
                />
              ))}

              {/* Pagination */}
              {data.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Page {data.pagination.page} of {data.pagination.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page === data.pagination.totalPages} onClick={() => setPage(page + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
